import { type RootState } from "~/app/store";
import { type LinearCstic } from "~/features/configurator/Linear/models";
import { type SubstepType } from "~/features/configurator/Linear/dtos";
import {
  CENTRAL_CONNECTORS,
  CSTIC_DESIGN_TYPE_NAME,
  CSTIC_VALUE_DESIGN_TYPE_KUVE,
  CSTIC_VALUE_VHOE_W,
  CSTIC_VHOE,
  MANUAL_CONNECTORS,
} from "~/features/configurator/Linear/constants";
import { createSelector } from "reselect";

import { uniqueValues } from "~/common/utils/misc";
import type { LinearConfiguratorState } from "~/features/configurator/Linear/redux/types";
import { REFERENCE_MATERIAL_CSTIC, UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { getConfiguratorDefinition } from "~/features/configurator/configuratorRegistry";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import { KmatType } from "~/features/configurator/Linear/kmatType";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";

const EMPTY_CSTIC_MAP: Dict<LinearCstic> = {};

export const getCsticMap = (kmatType: KmatType, index: number) => (state: RootState) =>
  getCsticMapFromNested(kmatType, index)(state.linearConfigurator);

export const getCsticMapFromNested = (kmatType: KmatType, index: number) => (state: LinearConfiguratorState) => {
  const kmatList = state.kmats[kmatType];
  if (!kmatList) {
    return EMPTY_CSTIC_MAP;
  }
  return kmatList[index] ? kmatList[index].cstics : EMPTY_CSTIC_MAP;
};

export function selectMainKmatCstics(state: RootState) {
  return getMainKmat({ kmats: state.linearConfigurator.kmats }).cstics;
}

export function selectSubKmat(kmatType: KmatType, index: number) {
  return (state: RootState) => getKmats(state)[kmatType]?.[index];
}

export function selectKmatAmount(kmatType: KmatType) {
  return (state: RootState) => getKmats(state)[kmatType]?.length ?? 0;
}

export function selectRailAmount(state: RootState) {
  return selectKmatAmount(KmatType.SubRail)(state);
}

export function selectCarriageAmount(state: RootState) {
  return selectKmatAmount(KmatType.SubCarriage)(state);
}

export function selectCarriagesPerGuidewayAmount(state: RootState) {
  const railAmount = selectRailAmount(state);
  const carriageAmount = selectCarriageAmount(state);
  return carriageAmount / railAmount;
}

export function selectSubKmatCstics(kmatType: KmatType, index: number) {
  return (state: RootState) => selectSubKmat(kmatType, index)(state).cstics;
}

export const getKmats = (state: RootState) => state.linearConfigurator.kmats;

export const getCurrentStep = (state: RootState) => state.linearConfigurator.currentStep;
export const getHighestVisitedStep = (state: RootState) => state.linearConfigurator.highTideStep;
export const getActiveTab = (state: RootState) => state.linearConfigurator.activeTab;
export const getSteps = (state: RootState) => state.linearConfigurator.steps;
export const getSubstepStep = (type: SubstepType) => (state: RootState) => state.linearConfigurator.substeps[type];
export const shouldEnableConfigureCarriageValuesLinear = (state: RootState) =>
  state.linearConfigurator.enableCarriageEdit;

export const getCadConfig = (state: RootState) => {
  const tempCadInfo = {
    url: state.linearConfigurator.cadUrl,
    xml: state.linearConfigurator.cadXmlRequest,
  };

  return tempCadInfo;
};

export const getMinStepToSave = (state: RootState): number => {
  const defaultKmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
  const designTypeCstic = defaultKmat.cstics?.[CSTIC_DESIGN_TYPE_NAME];
  const sectionCstic = defaultKmat.cstics?.[CSTIC_VHOE];
  if (
    designTypeCstic &&
    designTypeCstic.value &&
    designTypeCstic.value == CSTIC_VALUE_DESIGN_TYPE_KUVE &&
    sectionCstic &&
    sectionCstic.value &&
    sectionCstic.value == CSTIC_VALUE_VHOE_W
  ) {
    return 4; // 0-based, so 5th step
  }
  return 3; // 0-based, so 4th step
};

export const getShowLoader = (state: RootState) => state.linearConfigurator.showLoader;
export const getIsCompleteComplete = (state: RootState) => state.linearConfigurator.isConfigComplete;

export const getProductCode = (state: RootState) => state.linearConfigurator.productCode;
export const getConfigId = (state: RootState) => state.linearConfigurator.configId;
export const getConfiguratorType = (state: RootState) => state.linearConfigurator.type;
export const getSystemLayout = (state: RootState) => state.linearConfigurator.substeps.SystemLayout;
export const selectedPriceReference = (state: RootState) => state.linearConfigurator.priceReference;
export const selectProductImageUrl = (state: RootState) => {
  if (
    state.linearConfigurator.type === ConfiguratorType.System &&
    state.linearConfigurator.productCode === UNIT_CONFIGURATOR_PRODUCT_CODE
  ) {
    const systemLayout = getSystemLayout(state);
    const tileSelectedInFirstStep = systemLayout.selectedTile;
    return systemLayout.tiles[tileSelectedInFirstStep!].imageUrl;
  }

  return state.linearConfigurator.productImageUrl;
};

export const selectPieceLimits =
  (guidewayIndex: number) =>
  (state: RootState): { min: number; max: number } | undefined => {
    const limits = state.linearConfigurator.guidewayPieceLimits;
    return limits ? limits[guidewayIndex] : undefined;
  };

export const getMinLength = (state: RootState) => state.linearConfigurator.minLength;
export const getMaxLenth = (state: RootState) => state.linearConfigurator.maxLength;

export const getIsInConflict = (state: RootState) => {
  if (state.linearConfigurator.conflicts.length === 0) {
    return false;
  }
  const isConflictAvoidable = getConfiguratorDefinition(state.linearConfigurator.productCode).isConflictAvoidable;
  if (!isConflictAvoidable) {
    return false;
  }
  return !state.linearConfigurator.conflicts[0].cstics.some(({ name }) => isConflictAvoidable(name));
};

export const selectConflictedCstics = createSelector(
  (state: RootState) => state.linearConfigurator.conflicts,
  (conflicts) => {
    return uniqueValues(conflicts.flatMap((c) => c.cstics.map((c) => c.name)));
  },
);
export const getHelpUrl = (state: RootState) => state.linearConfigurator.helpUrl;

export const selectBaseDesignation = (state: RootState) => {
  const configurator = getConfiguratorDefinition(state.linearConfigurator.productCode);
  if (configurator?.generateDesignation) {
    const kmats = state.linearConfigurator.kmats;
    return configurator.generateDesignation(kmats, state.linearConfigurator);
  }

  return "N/A";
};

export const selectCanQueryAvailability = (state: RootState) => {
  const configurator = getConfiguratorDefinition(state.linearConfigurator.productCode);
  if (configurator?.canQueryAvailability) {
    return configurator.canQueryAvailability(state);
  }

  return true;
};

export const selectCanQueryPrice = (state: RootState) => {
  const configurator = getConfiguratorDefinition(state.linearConfigurator.productCode);
  if (configurator?.canQueryPrice) {
    return configurator.canQueryPrice(state);
  }

  return true;
};

export const selectInvalidSubsteps = createSelector(
  (state: RootState) => state.linearConfigurator.currentStep,
  (state: RootState) => state.linearConfigurator.steps,
  (state: RootState) => state.linearConfigurator.substeps,
  (currentStep, steps, substeps) => {
    const step = steps[currentStep];
    return step.substeps.map((substepType) => substeps[substepType]).filter((substep) => substep.isInvalid);
  },
);

export const isCurrentStepValid = (state: RootState) => {
  return selectInvalidSubsteps(state).length === 0;
};

export const selectIsConfiguratorAddToCartEnabled = (state: RootState) => {
  const configurator = getConfiguratorDefinition(state.linearConfigurator.productCode);
  if (!configurator.isAddToCartEnabled) {
    return false;
  }
  return configurator.isAddToCartEnabled(state) && isCurrentStepValid(state);
};

export const selectIsSaveConfigurationEnabled = (state: RootState) => {
  // Two conditions need to be fulfilled in order for the save button to be enabled
  // 1. Current step should be higher or at least equal to MIN_STEP_TO_SAVE (depends on configurator type)
  // 2. The current configuration should be valid
  const configurator = getConfiguratorDefinition(state.linearConfigurator.productCode);
  if (!configurator.isSaveEnabled) {
    return false;
  }
  return configurator.isSaveEnabled(state) && isCurrentStepValid(state);
};
export const selectSchmieranschlussTabConfig = (state: RootState) => {
  return [
    {
      tabName: "page.lc.schmieranchluss.tab1",
      values: MANUAL_CONNECTORS,
    },
    {
      tabName: "page.lc.schmieranchluss.tab2",
      values: CENTRAL_CONNECTORS,
    },
  ];
};
export const selectAdvancedTranslation = (key: string) => (state: RootState) =>
  state.linearConfigurator.advancedSpecialTranslations[key];

export const getIsCustomSegmentLengthSelected = (kmatIndex: number) => (state: RootState) => {
  if (kmatIndex) {
    return state.linearConfigurator.customSecondSegmentLengthSelected;
  }
  return state.linearConfigurator.customSegmentLengthSelected;
};

export const selectIsConfiguratorAddedToCartAlready = (state: RootState) => state.linearConfigurator.addedToCart;

export const selectReferenceMaterial = (state: RootState) => {
  const cstics = getMainKmat({ kmats: state.linearConfigurator.kmats }).cstics;
  return cstics[REFERENCE_MATERIAL_CSTIC]?.value;
};

export const selectKbHash = (state: RootState) => {
  return Object.values(state.linearConfigurator.kmats)
    .flatMap((x) => x)
    .flatMap((x) => Object.values(x.cstics))
    .map((cstic) => cstic.value ?? "")
    .join("-");
};

export const selectAllSteps = (state: RootState) => state.linearConfigurator.steps;

export const selectRailKmatType = (state: RootState) => {
  return selectDeepRailKmatType(state.linearConfigurator);
};

export const selectCarriageKmatType = (state: RootState) => {
  return selectDeepCarriageKmatType(state.linearConfigurator);
};

export const selectDeepRailKmatType = (state: LinearConfiguratorState) => {
  const kmats = state.kmats;
  if (kmats[KmatType.SubRail]) {
    return KmatType.SubRail;
  } else {
    return KmatType.Main;
  }
};

export const selectDeepCarriageKmatType = (state: LinearConfiguratorState) => {
  const kmats = state.kmats;
  if (kmats[KmatType.SubCarriage]) {
    return KmatType.SubCarriage;
  } else {
    return KmatType.Main;
  }
};
